import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  DOCUMENT,
  inject
} from '@angular/core';
import {
  FactsheetActionNextButtonFactsheet,
  selectorFactsheetFactsheet
} from '@bepatient/factsheet-store';
import { getAccessibleTextColor } from '@bepatient/shared';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { DynamicFormComponentV3 } from '../dynamic-form/dynamic-form.component';

@Component({
  selector: 'bepatient-dynamic-form-controls-v3',
  templateUrl: './dynamic-form-controls.component.html',
  styleUrls: ['./dynamic-form-controls.component.scss'],
  standalone: false
})
export class DynamicFormControlsComponentV3 implements OnDestroy {
  document = inject(DOCUMENT);
  private store = inject<Store<any>>(Store);

  lastPage: boolean;
  firstPage: boolean;
  currentPageNum: number;

  private color: string;
  private subscription: Subscription;

  @Input() readonly = false;

  @Input() hideSelector = false;

  @Output() prev = new EventEmitter<boolean>();
  @Output() next = new EventEmitter<boolean>();

  @Input() isFactsheet = false;
  @Input() factsheetColor: string;

  @Input() set form(v: DynamicFormComponentV3) {
    this.color = v.color;
    this.unsubscribe();
    this.subscription = v.progressChange
      .pipe(startWith(v.progress))
      .subscribe(([currentPage, pageCount]) => {
        // No more pages after current page -> last page true/false
        let noMorePages: boolean = false;
        // Array of page status for skipping page after current page
        let pagesAfterCurrentPage: Array<boolean>;
        if (
          v &&
          v.skipPage &&
          v.skipPage.length > 0 &&
          currentPage < pageCount
        ) {
          // Get all pages skip status after current page
          pagesAfterCurrentPage = v.skipPage.slice(currentPage + 1);
          // Check if all the pages after current page are skipped and set noMorePages true/false
          noMorePages = pagesAfterCurrentPage.every(
            skipStatus => skipStatus === true
          );
        }

        /**
         * To set last page check,
         * If currentPage is last page set lastPage true
         * Else if, noMorePages is true then set lastPage true
         *
         */
        this.lastPage = currentPage === pageCount - 1 || noMorePages;
        this.firstPage = currentPage === 0;
      });
  }

  @ViewChild('prevBtn', { read: ElementRef }) set prevBtn(v: ElementRef) {
    this.setColor(v, true);
  }
  @ViewChild('saveBtn', { read: ElementRef }) set saveBtn(v: ElementRef) {
    this.setColor(v);
  }
  @ViewChild('nextBtn', { read: ElementRef }) set nextBtn(v: ElementRef) {
    this.setColor(v);
  }

  @ViewChild('nextButton', { read: ElementRef }) set nextButton(v: ElementRef) {
    this.setColor(v);
  }

  ngOnDestroy() {
    this.unsubscribe();
  }

  nextFactsheet(): void {
    let id: string;
    this.store
      .pipe(select(selectorFactsheetFactsheet))
      .subscribe(v => {
        id = v.id;
      })
      .unsubscribe();
    this.store.dispatch(new FactsheetActionNextButtonFactsheet({ id: id }));
  }

  private unsubscribe(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private setColor(element: ElementRef, reverse = false): void {
    if (element) {
      const style = element.nativeElement.style;
      const colors = ['var(--ion-color-primary-contrast)', this.color];

      if (reverse) {
        colors.reverse();
      }

      if (this.isFactsheet) {
        colors[1] = this.factsheetColor;
      }

      // Filled buttons (save / next) put the label on the study color, so the
      // fixed primary-contrast token can be illegible when the study color
      // differs from primary. Derive the label color from the actual
      // background via the canonical WCAG-AA utility. The reversed (prev)
      // button keeps its outline style (its background is a CSS variable that
      // cannot be measured). See docs/adr/0001-contrast-utility-consolidation.md.
      const background = colors[1];
      if (!reverse && background && !background.includes('var(')) {
        try {
          colors[0] = getAccessibleTextColor(background);
        } catch {
          // Leave the primary-contrast fallback if the color can't be parsed.
        }
      }

      style.setProperty('--color', colors[0]);
      style.setProperty('--color-activated', colors[0]);
      style.setProperty('--color-focused', colors[0]);
      style.setProperty('--background', colors[1]);
      style.setProperty('--background-activated', colors[1]);
      style.setProperty('--background-focused', colors[1]);
    }
  }
}
